import AnalyticsWebClient from '@atlassiansox/analytics-web-client/client';
import {
	envType,
	tenantType,
	userType,
} from '@atlassiansox/analytics-web-client/analytics-web-types';

import type { ExperienceTracker } from '@atlassian/experience-tracker/ExperienceTracker';
import {
	AppNames,
	getAppName,
	getConfig,
	getEnv,
	type MicrosEnv,
} from '@atlassian/teams-common/context-config';
import { payloadPublisher } from '@atlassian/ufo';

import {
	analyticsKeyToEventMap,
	type CommonEvent,
	type ScreenEvent,
} from '../components/AnalyticsContext';
import { type Analytics } from '../components/AnalyticsContext/types';
import { configureNewTracker } from '../monitor/experience-tracker';
import referrerInfo from '../utils/referrerInfo';

function getServerName() {
	return window.location.hostname;
}

const { LOCAL, DEV, STAGING, PROD } = envType;
// disabled because of linting error that upon fixing, breaks the typing
const ENV_MAP: Record<string, envType> = {
	local: LOCAL,
	test: LOCAL,
	adev: DEV,
	'stg-east': STAGING,
	stg: STAGING,
};

// disabled because of linting error that upon fixing, breaks the typing
const getEnvType = (env: 'local' | MicrosEnv): envType => {
	return ENV_MAP[env] || PROD;
};

const analyticsConfig = getConfig().analytics;
const SUB_PRODUCT_DEFAULT = 'siteDirectory';
const PRODUCT_DEFAULT = 'people';

type Options = any; // tslint:disable-line no-any
type ScreenEventType = 'screen';
type EventTypesWithoutScreen = 'track' | 'operation' | 'ui';
type EventType = EventTypesWithoutScreen | ScreenEventType;
export type CallBackFn = () => void;

interface CustomOptions {
	commonProperties?: { [key: string]: any }; // tslint:disable-line no-any
	eventPrefix?: string;
	subproduct?: string;
}

type QueuedEvent = {
	eventCallback?: CallBackFn;
} & (
	| {
			eventType: ScreenEventType;
			eventData: ScreenEvent;
	  }
	| {
			eventType: EventTypesWithoutScreen;
			eventData: CommonEvent;
	  }
);
interface State {
	analyticsscheme: string;
	analyticsserver: string;
	server: string;
	product: string;
	isReadyToSend: boolean;
	pendingEventsQueue: QueuedEvent[];
	storage_key: string;
}

class AnalyticsClient implements Analytics {
	cloudId: string;
	teamId?: string;
	consumer?: string;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	commonProperties: { [key: string]: any };
	eventPrefix: string;
	state: State & Options;

	// disabled because of linting error that upon fixing, breaks the typing
	analyticsWebClient: AnalyticsWebClient;
	_expTracker: ExperienceTracker;

	constructor(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		options: any = {},
		{
			commonProperties = {},
			eventPrefix = 'atlassian.people',
			subproduct = SUB_PRODUCT_DEFAULT,
			cloudId = '',
		}: {
			commonProperties?: {} | undefined;
			eventPrefix?: string | undefined;
			subproduct?: string | undefined;
			cloudId?: string | undefined;
		} = {},
	) {
		const customOptions: CustomOptions = {
			commonProperties,
			eventPrefix,
		};

		this.cloudId = cloudId;
		this.commonProperties = customOptions.commonProperties!;
		this.eventPrefix = customOptions.eventPrefix!;

		this.state = {
			analyticsscheme: analyticsConfig.scheme,
			analyticsserver: analyticsConfig.server,
			server: getServerName(),
			product: PRODUCT_DEFAULT,
			isReadyToSend: false,
			pendingEventsQueue: [],
			storage_key: 'atlassian.people.analytics',

			...options,
		};

		// New GAS pipeline client
		this.analyticsWebClient = new AnalyticsWebClient(
			{
				env: getEnvType(getEnv()),
				product: 'people',
				subproduct,
			},
			{},
		);

		this.initUFO();

		this._expTracker = configureNewTracker(this, this.analyticsWebClient);
	}

	setTeamId(teamId?: string): void {
		this.teamId = teamId;
	}

	setConsumer(consumer?: string): void {
		this.consumer = consumer;
	}

	initUFO(): void {
		if (getAppName() !== AppNames.STANDALONE_DIRECTORY) {
			return;
		}

		payloadPublisher.setup({
			gasv3: this.analyticsWebClient,
			product: 'people',
			app: {
				// @ts-ignore
				version: { web: __VERSION__ },
			},
			region: getEnv(),
		});

		// import { ufologger } from @atlassian/ufo and uncomment the following line to see UFO logs:
		// ufologger.enable();
	}

	getAnalyticsWebClient(): AnalyticsWebClient {
		return this.analyticsWebClient;
	}

	expTracker(): ExperienceTracker {
		return this._expTracker;
	}

	sendTrackEvent(eventData: CommonEvent): void {
		this.analyticsWebClient.sendTrackEvent(eventData);
	}

	/**
	 * Checks whether the library has been initialized and decides whether the event should be sent or cached
	 * @param {*} eventType Values could be 'track', 'screen', 'operation, 'ui'
	 * @param {*} eventData Data of the event
	 * @param {Function} cb Callback is called when the event is sent successfully.
	 */
	private canEventBeSent(
		eventType: EventType,
		eventData: CommonEvent | ScreenEvent,
		cb?: CallBackFn,
	): boolean {
		if (this.state.isReadyToSend) {
			return true;
		}

		// add to `pendingEventsQueue` in order to send the event later
		this.state.pendingEventsQueue.push({
			eventType,
			eventData,
			// `eventCallback` is called when `pendingEventsQueue` is processed and eventData is sent
			eventCallback: cb,
		});

		return false;
	}

	pushTrackEvent(eventData: CommonEvent, cb?: CallBackFn): void {
		if (this.canEventBeSent('track', eventData, cb)) {
			const event = this.buildEventPayload(eventData);
			this.analyticsWebClient.sendTrackEvent(event, cb);
		}
	}

	pushUIEvent(eventData: CommonEvent, cb?: CallBackFn): void {
		if (this.canEventBeSent('ui', eventData, cb)) {
			const event = this.buildEventPayload(eventData);
			this.analyticsWebClient.sendUIEvent(event, cb);
		}
	}

	pushOperationalEvent(eventData: CommonEvent, cb?: CallBackFn): void {
		if (this.canEventBeSent('operation', eventData, cb)) {
			const event = this.buildEventPayload(eventData);
			this.analyticsWebClient.sendOperationalEvent(event, cb);
		}
	}

	pushScreenEvent(eventData: ScreenEvent, cb?: CallBackFn): void {
		const additionalAttributes = referrerInfo.get();
		referrerInfo.markUsed();

		if (eventData && additionalAttributes) {
			eventData = {
				...eventData,
				attributes: {
					...additionalAttributes,
					...eventData.attributes,
				},
			};
		}

		if (this.canEventBeSent('screen', eventData, cb)) {
			this.analyticsWebClient.sendScreenEvent(eventData, cb);
		}
	}

	buildEventPayload(eventData: Partial<CommonEvent>): CommonEvent {
		const attributes =
			this.teamId || this.consumer || eventData.attributes
				? {
						...(this.teamId && { teamId: this.teamId }),
						...(this.consumer && { consumer: this.consumer }),
						...eventData.attributes,
					}
				: null;
		return {
			containerType: null,
			containerId: null,
			objectType: null,
			objectId: null,
			source: null,

			actionSubject: null,
			action: null,
			actionSubjectId: null,
			tags: [],
			...eventData,
			attributes,
		} as CommonEvent;
	}

	mapAnalyticsKeyToEvent(analyticsKey: string): string | null {
		return analyticsKeyToEventMap[analyticsKey] || null;
	}

	/**
	 *  Start up E-MAU events in the new analytics pipeline (GAS v3)
	 *  See: https://extranet.atlassian.com/display/BPO/DACI%3A+E-MAU+Definition
	 */
	async startUIViewedEvent(principalUserId: string): Promise<void> {
		if (!principalUserId) {
			return;
		}

		this.analyticsWebClient.setTenantInfo(tenantType.CLOUD_ID, this.cloudId || '');
		this.analyticsWebClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, principalUserId);

		this.analyticsWebClient.startUIViewedEvent();
		this.state.isReadyToSend = true;
		this.flushPendingEventsQueue();
	}

	/**
	 * Flush all the pending events in the queue
	 */
	flushPendingEventsQueue(): void {
		this.state.pendingEventsQueue.forEach((element: QueuedEvent) => {
			const { eventType, eventData, eventCallback } = element;

			switch (eventType) {
				case 'track':
					this.pushTrackEvent(eventData, eventCallback);
					break;
				case 'ui':
					this.pushUIEvent(eventData, eventCallback);
					break;
				case 'screen':
					this.pushScreenEvent(eventData, eventCallback);
					break;
				case 'operation':
					this.pushOperationalEvent(eventData, eventCallback);
					break;
				default:
					// eslint-disable-next-line no-console
					console.error('Invalide Event :', element);
					break;
			}
		});
	}
}

export default AnalyticsClient;

let preservedAnaylticsClient: AnalyticsClient;

/**
 * @private
 * @deprecated Analytics events should be fired using the `@atlaskit/teams-app-internal-analytics` package.
 *
 * Cloud Id is a required data to create analytics client instance.
 * The instance will be cached and reused when `getAnalyticsClient` is called multiple times.
 * @param mode
 * @param subproduct
 * @param cloudId
 */
export const getAnalyticsClient = ({
	mode,
	subproduct,
	cloudId,
}: {
	mode: string;
	subproduct: string;
	cloudId?: string;
}): AnalyticsClient | undefined => {
	if (!cloudId) {
		return;
	}

	if (preservedAnaylticsClient) {
		return preservedAnaylticsClient;
	}

	preservedAnaylticsClient = new AnalyticsClient(
		{},
		{
			commonProperties: { mode },
			subproduct,
			cloudId,
		},
	);

	return preservedAnaylticsClient;
};
